import {ContactBulkUploadResponse, ContactUpload} from "@/types/contacts";
import { apiFetch } from "@/lib/api";
import {GAIL_API} from "@/utils/common/constants/routes";

// Galactus' `bulk_add` endpoints sit behind Vercel's 4.5MB serverless body
// cap. We budget by serialized byte size rather than row count so rows with
// heavy free-form fields (notes, custom data) can't quietly push a batch
// over the limit. 3.5MB leaves ~1MB headroom for the JSON envelope, request
// headers, and any compression overhead at the edge.
export const BULK_ADD_MAX_BYTES = 3_500_000;
export const BULK_ADD_MAX_CONCURRENCY = 4;

// Shared encoder so we don't allocate one per item — chunking a 10k-row
// upload calls this thousands of times.
const byteSizeEncoder = new TextEncoder();

// Returns the UTF-8 byte length of the serialized item, not the JS string
// `.length` (which counts UTF-16 code units and undercounts multi-byte
// characters like CJK or emoji — those can be 2-4× the .length value and
// can push a "safe" 3.5MB batch over Vercel's 4.5MB body cap).
export function defaultItemByteSize<T>(item: T): number {
   return byteSizeEncoder.encode(JSON.stringify(item)).length;
}

// Split items into batches whose serialized size stays under `maxBytes`.
// An item larger than `maxBytes` on its own still gets its own batch — the
// upstream will reject it, but we don't silently drop it.
export function chunkByBytes<T>(
   items: readonly T[],
   maxBytes: number,
   sizeFn: (item: T) => number = defaultItemByteSize
): T[][] {
   if (items.length === 0) return [];
   const out: T[][] = [];
   let current: T[] = [];
   let currentBytes = 0;
   for (const item of items) {
      const itemBytes = sizeFn(item);
      if (current.length > 0 && currentBytes + itemBytes > maxBytes) {
         out.push(current);
         current = [];
         currentBytes = 0;
      }
      current.push(item);
      currentBytes += itemBytes;
   }
   if (current.length > 0) out.push(current);
   return out;
}

export type ProgressCallback = (completed: number, total: number) => void;

export interface RunChunkedOptions {
   concurrency?: number;
   // Called after each batch *successfully resolves*. `completed` is the
   // running count of resolved batches; `total` is `batches.length`. If
   // `fn` throws, the whole pool rejects and no further callbacks fire —
   // the callback never sees the failing batch. Use this hook for
   // progress UI, not for error reporting.
   onBatchComplete?: ProgressCallback;
}

// Run `fn` against each batch with at most `concurrency` in flight at once.
// Results are returned in batch order regardless of completion order. If `fn`
// throws, the whole pool rejects with that error — callers that want
// continue-on-error semantics should catch inside `fn` and return a sentinel.
export async function runChunked<T, R>(
   batches: readonly T[][],
   fn: (batch: T[], index: number) => Promise<R>,
   options: RunChunkedOptions = {}
): Promise<R[]> {
   const {concurrency = BULK_ADD_MAX_CONCURRENCY, onBatchComplete} = options;
   const results: R[] = new Array(batches.length);
   let next = 0;
   let completed = 0;
   const workerCount = Math.min(Math.max(1, concurrency), batches.length);
   const workers = Array.from({length: workerCount}, async () => {
      while (true) {
         const index = next++;
         if (index >= batches.length) return;
         results[index] = await fn(batches[index], index);
         completed++;
         onBatchComplete?.(completed, batches.length);
      }
   });
   await Promise.all(workers);
   return results;
}

// Build a same-shape ContactBulkUploadResponse for a batch whose request
// failed outright (e.g. api() returned null). If `errorDetail` is provided,
// each row in the batch is emitted as a GailError result so downstream code
// (failure modals, per-row reason rendering) can surface it; otherwise
// `results` is left empty for callers that only need the failure count.
export function synthesizeFailedBatchResponse(
   batchSize: number,
   errorDetail?: string
): ContactBulkUploadResponse {
   const results: ContactBulkUploadResponse["results"] = errorDetail
      ? Array.from({length: batchSize}, () => ({
           errors: [
              {
                 code: "batch_upload_failed",
                 detail: errorDetail,
                 status: "0",
                 title: errorDetail,
              },
           ],
        }))
      : [];
   return {succeeded: 0, failed: batchSize, results};
}

export function mergeBulkUploadResponses(
   responses: ContactBulkUploadResponse[]
): ContactBulkUploadResponse {
   return {
      succeeded: responses.reduce((sum, r) => sum + r.succeeded, 0),
      failed: responses.reduce((sum, r) => sum + r.failed, 0),
      results: responses.flatMap((r) => r.results),
   };
}

// POST leads to `/contacts/bulk_add` in byte-bounded chunks, run with
// bounded concurrency, and merge the per-batch responses. Any batch whose
// request fails outright is synthesized as a same-shape failure response so
// the merged result is always usable (callers branch on
// `merged.succeeded === 0` to detect total failure). When `batchFailureDetail`
// is provided, failed batches emit per-row GailError entries so failure
// modals can list which rows didn't upload.
export interface UploadLeadsOptions {
   batchFailureDetail?: string;
   maxBytes?: number;
   onBatchComplete?: ProgressCallback;
}

export async function uploadLeadsInChunks(
   leads: readonly ContactUpload[],
   options: UploadLeadsOptions = {}
): Promise<ContactBulkUploadResponse> {
   const {batchFailureDetail, maxBytes = BULK_ADD_MAX_BYTES, onBatchComplete} = options;
   const batches = chunkByBytes(leads, maxBytes);
   const responses = await runChunked(
      batches,
      (batch) =>
         apiFetch<ContactBulkUploadResponse>(
           `${GAIL_API}/v1/contacts/bulk_add`,
           "POST",
           {
           body: batch,
         },
         ),
      {onBatchComplete}
   );
   const filledResponses = responses.map(
      (response, index) =>
         response ??
         synthesizeFailedBatchResponse(batches[index].length, batchFailureDetail)
   );
   return mergeBulkUploadResponses(filledResponses);
}

export type AddToCampaignResult = {
   ok: boolean;
   failedBatches: number;
   totalBatches: number;
   // Best-effort upper bound on contacts that may not have been added: the
   // sum of batch sizes for any batch whose request did not succeed. The
   // backend reports per-row outcomes via 204 No Content on success, so we
   // can't know exactly which rows failed within a failed batch — assuming
   // the whole batch may have failed is the safe over-estimate for a
   // warning message.
   failedRowsUpperBound: number;
};

// Shared by the contacts and contact-list pages (and CampaignSetupStep step 2).
// Posts contact ids to `/campaigns/:id/contacts/bulk_add` in byte-bounded
// chunks. The endpoint returns 204 No Content on success.
//
// We use doNotParse + fallback=true so api() returns:
//   - 2xx success → null (counted as success)
//   - 4xx/5xx     → `true` fallback (counted as failure)
// Known limitation: a network-layer exception is swallowed by api() and also
// returns null, so we'd miscount it as success. Fixing this requires using
// apiFetchWithStatus and inspecting status directly — out of scope here; the
// original commit had the same flaw.
export interface AddContactsToCampaignOptions {
   maxBytes?: number;
   onBatchComplete?: ProgressCallback;
}

export async function addContactsToCampaignChunked(
   campaignId: string,
   contactIds: readonly string[],
   options: AddContactsToCampaignOptions = {}
): Promise<AddToCampaignResult> {
   const {maxBytes = BULK_ADD_MAX_BYTES, onBatchComplete} = options;
   const batches = chunkByBytes(
      contactIds.map((id) => ({contactId: id})),
      maxBytes
   );
   const responses = await runChunked(
      batches,
      (batch) =>
         apiFetch<true>(
           `${GAIL_API}/v1/campaigns/${campaignId}/contacts/bulk_add`,
           "POST",
           {
           body: batch,
           doNotParse: true,
           fallback: true,
         },
         ),
      {onBatchComplete}
   );
   let failedBatches = 0;
   let failedRowsUpperBound = 0;
   responses.forEach((response, index) => {
      if (response) {
         failedBatches++;
         failedRowsUpperBound += batches[index].length;
      }
   });
   return {
      ok: failedBatches === 0,
      failedBatches,
      totalBatches: batches.length,
      failedRowsUpperBound,
   };
}
